import { Fragment, useEffect, useState } from 'react'
import {
  api,
  describe,
  type AccountDetailBody,
  type AccountRow,
  type AccountsPage,
  type Advice,
  type ComposedEmail,
  type MemberDetail,
  type MemberQuery,
  type Member,
  type MembersPage,
  type Run,
  type SelectedBy,
  type Signal,
} from '../api'
import { useRefreshOnActivate } from '../useActive'

/**
 * Accounts — looking an account up, and what the engine knows about it.
 *
 * TWO SCOPES, and the default is the wider one.
 *
 * This screen could once only show the members of ONE saved list: you picked a
 * run, and you saw the people some filter had already matched. That answers
 * "who is in this segment" and nothing else — reaching a particular account
 * meant first knowing which of forty lists happened to contain it. So the
 * default is now the INDEX of every account we hold a snapshot for, and the
 * saved-list view stays behind a switch, because it is the only one that can
 * say which condition selected a row.
 *
 * NOTHING IS FETCHED UNTIL SEARCH IS PRESSED in the index. The list is tens of
 * thousands of rows and the operator arrives with a question — a domain, an
 * account id, "which of these are dormant" — so opening the tab is not the
 * moment to go and read all of it. The saved-list scope keeps loading on sight:
 * a run is a bounded thing somebody already chose.
 *
 * Masked by default in both, and the toggle is deliberately a visible act
 * rather than a remembered preference. These are real customers: a screen that
 * quietly came back unmasked because of something you clicked last week is a
 * screen that leaks by default.
 */

const COLUMNS: { key: string; label: string; kind?: 'num' }[] = [
  { key: 'onboarding_stage', label: 'Stage' },
  { key: 'has_subscription', label: 'Paying' },
  { key: 'has_verified_email', label: 'Verified' },
  { key: 'visits_30d', label: 'Visits 30d', kind: 'num' },
  { key: 'consents_30d', label: 'Consents 30d', kind: 'num' },
  { key: 'cycle_visits', label: 'Cycle', kind: 'num' },
  { key: 'visit_threshold', label: 'Quota', kind: 'num' },
  { key: 'days_since_visit', label: 'Days idle', kind: 'num' },
]

/** The CRM's onboarding stages, in funnel order. A closed set on that side, so
 *  listing them beats deriving options from whatever the current page happens
 *  to contain — a filter whose choices change as you page through it is worse
 *  than no filter. */
const STAGES = [
  'registered',
  'verified',
  'app_created',
  'receiving_traffic',
  'receiving_consents',
  'subscribed',
]

const PAGE = 100
/** Offered rather than fixed: 100 is a screenful to read, 500 is what somebody
 *  scanning for one domain wants. The server caps at 500 regardless. */
const PAGE_SIZES = [100, 250, 500]

/** Every filter is a string here, including the yes/no ones, because a select
 *  has three states and a boolean has two: '' is "any" and must widen. */
interface Filters {
  q: string
  stage: string
  paying: string
  verified: string
  source: string
  activated: string
  customized: string
  in_eu: string
  visits_min: string
  visits_max: string
  consents_min: string
  consents_max: string
  idle_min: string
  idle_max: string
  commercial_state: string
  funnel_stage: string
  traffic_band: string
  engagement_recency: string
  quota_pressure: string
}

const NO_FILTERS: Filters = {
  q: '', stage: '', paying: '', verified: '', source: '',
  activated: '', customized: '', in_eu: '',
  visits_min: '', visits_max: '', consents_min: '', consents_max: '',
  idle_min: '', idle_max: '',
  commercial_state: '', funnel_stage: '', traffic_band: '',
  engagement_recency: '', quota_pressure: '',
}

/** The derived bands, with their values. These are the vocabulary the segment
 *  conditions are written in — "which of these are dormant" is a question about
 *  a band, not about a column — which is why they are worth the pass over the
 *  run that answering them costs. */
const BANDS: { key: keyof Filters; label: string; options: string[] }[] = [
  { key: 'commercial_state', label: 'Commercial', options: ['paying', 'churned', 'never_paid'] },
  {
    key: 'funnel_stage',
    label: 'Funnel',
    options: ['not_installed', 'silent_banner', 'activated', 'subscribed'],
  },
  { key: 'traffic_band', label: 'Traffic', options: ['dead', 'low', 'medium', 'high'] },
  {
    key: 'engagement_recency',
    label: 'Last seen',
    options: ['never', 'active', 'cooling', 'dormant', 'gone'],
  },
  {
    key: 'quota_pressure',
    label: 'Quota',
    options: ['none', 'comfortable', 'approaching', 'over'],
  },
]

const YES_NO: { key: keyof Filters; label: string }[] = [
  { key: 'activated', label: 'Ever activated' },
  { key: 'customized', label: 'Banner customised' },
  { key: 'in_eu', label: 'Company in EU' },
]

const RANGES: { min: keyof Filters; max: keyof Filters; label: string }[] = [
  { min: 'visits_min', max: 'visits_max', label: 'Visits 30d' },
  { min: 'consents_min', max: 'consents_max', label: 'Consents 30d' },
  { min: 'idle_min', max: 'idle_max', label: 'Days idle' },
]

type Scope = 'all' | 'list'

/** What a run of the index was asked for.
 *
 *  `count` is the question somebody asks FIRST — is this filter the one I
 *  meant, is it four accounts or four thousand — and it is answerable without
 *  reading a single snapshot. Fetching a hundred jsonb documents and rendering
 *  them to answer "how many" is doing the expensive half of the work for
 *  something nobody is going to read. */
type Mode = 'page' | 'count'

export function AccountsTab({ active = true }: { active?: boolean }) {
  const [scope, setScope] = useState<Scope>('all')
  const [unmasked, setUnmasked] = useState(false)

  // Both scopes stay MOUNTED, hidden rather than unmounted, for the reason the
  // shell keeps its tabs mounted: each holds a searched page, an open row and a
  // scroll position, and switching to check something in the other one should
  // not cost them.
  return (
    <>
      <div className="panel">
        <h2>Accounts</h2>
        <div className="panel-body">
          <div className="row-form">
            <div>
              <label>Looking at</label>
              <div className="row" style={{ gap: 6 }}>
                <button
                  aria-current={scope === 'all'}
                  className={scope === 'all' ? 'primary' : undefined}
                  onClick={() => setScope('all')}
                >
                  All accounts
                </button>
                <button
                  aria-current={scope === 'list'}
                  className={scope === 'list' ? 'primary' : undefined}
                  onClick={() => setScope('list')}
                >
                  A saved list
                </button>
              </div>
            </div>
            <div>
              <label className="check">
                <input
                  type="checkbox"
                  checked={unmasked}
                  style={{ width: 'auto' }}
                  onChange={(e) => setUnmasked(e.target.checked)}
                />
                Show real names and domains
              </label>
            </div>
          </div>
          <p className="hint">
            {scope === 'all' ? (
              <>
                Every account we hold a snapshot for, whichever list it was cut into, shown as the
                newest picture we have of it. <strong>Nothing is loaded until you search</strong> —
                set a filter and press Search. This is not the whole CRM: it is the accounts some
                filter has put in a list at some point.
              </>
            ) : (
              <>
                The members of one saved list. The only view that can say{' '}
                <strong>which condition selected a row</strong>, because away from a particular run
                an account may belong to six lists cut by four different rules.
              </>
            )}
          </p>
          <p className="hint">
            {unmasked ? (
              <strong>
                Showing real customer data — names, companies and domains. Every number that decides
                segment membership is shown either way, so masking does not hide anything you need to
                check a filter.
              </strong>
            ) : (
              <>
                Identity is masked. The values that determine which segment an account belongs to are
                all still here, so a row can be checked against the condition that selected it
                without exposing who they are. Search still matches names and domains — masking is
                what the screen shows, not what you may look for.
              </>
            )}
          </p>
        </div>
      </div>

      <div hidden={scope !== 'all'}>
        <AccountsIndex unmasked={unmasked} />
      </div>
      <div hidden={scope !== 'list'}>
        <SavedListAccounts active={active && scope === 'list'} unmasked={unmasked} />
      </div>
    </>
  )
}

/**
 * The index: every account we hold, searched on demand.
 *
 * `applied` is deliberately separate from the form. Paging must re-query with
 * the filters that produced the page, not with whatever is half-typed in the
 * boxes — otherwise pressing Next after starting to edit a filter silently
 * jumps to page 2 of a different query, and the row counts stop agreeing with
 * the rows. `null` means nobody has searched yet, which is a different state
 * from "searched and found nothing" and reads differently on screen.
 */
function AccountsIndex({ unmasked }: { unmasked: boolean }) {
  const [form, setForm] = useState<Filters>(NO_FILTERS)
  // What was actually run, and whether it asked for rows or only for a number.
  // `null` means nobody has run anything yet.
  const [request, setRequest] = useState<{ filters: Filters; mode: Mode } | null>(null)
  const [size, setSize] = useState(PAGE)
  const [offset, setOffset] = useState(0)
  const [page, setPage] = useState<AccountsPage | null>(null)
  const [expanded, setExpanded] = useState<number | null>(null)
  const [more, setMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (request === null) return
    const counting = request.mode === 'count'
    let live = true
    setLoading(true)
    void (async () => {
      try {
        const result = await api.accounts({
          ...toQuery(request.filters, false),
          limit: size,
          offset,
          masked: !unmasked,
          count_only: counting || undefined,
        })
        if (!live) return
        setError(null)
        setPage(result)
      } catch (err) {
        if (live) setError(describe(err))
      } finally {
        if (live) setLoading(false)
      }
    })()
    return () => {
      live = false
    }
  }, [request, offset, size, unmasked])

  const setFilter = (patch: Partial<Filters>) => setForm((f) => ({ ...f, ...patch }))
  const narrowed = Object.values(form).some((v) => v !== '')

  // A new object every time, so pressing either button again re-runs the same
  // query — which is what somebody who has just changed a list on another tab
  // means by pressing it.
  const run = (mode: Mode) => {
    setRequest({ filters: { ...form }, mode })
    setOffset(0)
    setExpanded(null)
  }

  return (
    <div className="panel">
      <h2>
        All accounts
        {page && (
          <span className="count">
            {page.total.toLocaleString()} matching · {page.total_accounts.toLocaleString()} held
          </span>
        )}
      </h2>
      <div className="panel-body">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            run('page')
          }}
        >
          <AccountFilters
            filters={form}
            setFilter={setFilter}
            more={more}
            setMore={setMore}
            narrowed={narrowed}
            onClear={() => setForm(NO_FILTERS)}
            showSource={false}
            trailing={
              <>
                <div>
                  <label htmlFor="page-size">Per page</label>
                  <select
                    id="page-size"
                    value={size}
                    onChange={(e) => {
                      setSize(Number(e.target.value))
                      setOffset(0)
                    }}
                  >
                    {PAGE_SIZES.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label>&nbsp;</label>
                  <button className="primary" type="submit" disabled={loading}>
                    {loading && request?.mode === 'page' ? (
                      <span className="row" style={{ gap: 7 }}>
                        <span className="spinner" /> Searching…
                      </span>
                    ) : (
                      'Search'
                    )}
                  </button>
                </div>
                <div>
                  <label>&nbsp;</label>
                  <button
                    type="button"
                    disabled={loading}
                    title="Run the same filters and report how many match, without reading a single account"
                    onClick={() => run('count')}
                  >
                    {loading && request?.mode === 'count' ? (
                      <span className="row" style={{ gap: 7 }}>
                        <span className="spinner" /> Counting…
                      </span>
                    ) : (
                      'Count only'
                    )}
                  </button>
                </div>
              </>
            }
          />
        </form>
      </div>

      {error && <div className="error">{error}</div>}

      {request === null ? (
        <div className="empty">
          Nothing is loaded yet. Set a filter — a domain, an account id, a band — and press Search,
          or <strong>Count only</strong> to see how many match without loading them.
        </div>
      ) : loading || !page ? (
        <div className="loading">
          {request.mode === 'count' ? 'Counting accounts…' : 'Searching accounts…'}
        </div>
      ) : page.counted_only ? (
        // A count, and it says so. An empty `accounts` here means nothing was
        // READ, not that nothing matched, and the two must not look alike.
        <div className="empty">
          <strong>
            {page.total.toLocaleString()} account{page.total === 1 ? '' : 's'} match
            {page.total === 1 ? 'es' : ''} those filters
          </strong>
          , of {page.total_accounts.toLocaleString()} we hold. Nothing was loaded —{' '}
          <button
            className="linkish"
            // THE COUNTED FILTERS, not whatever is in the boxes now. Reading
            // "1,204 match" and clicking through must list those 1,204, even
            // if somebody has started typing a different filter since.
            onClick={() => {
              setRequest({ filters: request.filters, mode: 'page' })
              setOffset(0)
            }}
          >
            list them
          </button>
          .
        </div>
      ) : page.accounts.length === 0 ? (
        <div className="empty">No account we hold matches those filters.</div>
      ) : (
        <>
          <AccountTable
            rows={page.accounts}
            unmasked={unmasked}
            expanded={expanded}
            setExpanded={setExpanded}
            runId={null}
          />
          <Pager offset={offset} size={size} total={page.total} onOffset={setOffset} />
        </>
      )}
    </div>
  )
}

/** The saved-list view — the screen as it was, minus the mask toggle, which is
 *  now shared with the index above it. */
function SavedListAccounts({ active, unmasked }: { active: boolean; unmasked: boolean }) {
  const [runs, setRuns] = useState<Run[] | null>(null)
  const [runId, setRunId] = useState<number | null>(null)
  const [page, setPage] = useState<MembersPage | null>(null)
  const [offset, setOffset] = useState(0)
  const [filters, setFilters] = useState<Filters>(NO_FILTERS)
  const [expanded, setExpanded] = useState<number | null>(null)
  const [more, setMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const loadRuns = async () => {
    try {
      const r = await api.runs()
      setRuns(r)
      // Default only when nothing is selected. On a refresh the operator may
      // be paged deep into a list; re-selecting would throw that away.
      setRunId((current) => current ?? r.find((x) => x.member_count > 0)?.id ?? null)
    } catch (err) {
      setError(describe(err))
    }
  }

  useEffect(() => {
    void loadRuns()
  }, [])

  // Saved lists are written by the People tab, so the picker refreshes on
  // return — keeping the chosen list, the page offset and the masking toggle.
  useRefreshOnActivate(active, loadRuns)

  // Debounced, because `q` fires on every keystroke and each one is a query
  // against a table that can hold twenty thousand rows. A run is a bounded
  // thing somebody already chose, so this one does load on sight.
  useEffect(() => {
    if (runId == null) return
    const timer = window.setTimeout(() => {
      setLoading(true)
      void (async () => {
        try {
          setError(null)
          setPage(
            await api.members(runId, {
              ...toQuery(filters, true),
              limit: PAGE,
              offset,
              masked: !unmasked,
            }),
          )
        } catch (err) {
          setError(describe(err))
        } finally {
          setLoading(false)
        }
      })()
    }, filters.q ? 250 : 0)
    return () => window.clearTimeout(timer)
  }, [runId, offset, unmasked, filters])

  if (error) return <div className="error">{error}</div>
  if (!runs) return <div className="loading">Loading…</div>

  const populated = runs.filter((r) => r.member_count > 0)
  const narrowed = Object.values(filters).some((v) => v !== '')

  // A filter change that leaves you on page 3 of a 1-page result shows nothing
  // and looks like an empty list.
  const setFilter = (patch: Partial<Filters>) => {
    setFilters((f) => ({ ...f, ...patch }))
    setOffset(0)
    setExpanded(null)
  }

  return (
    <div className="panel">
      <h2>
        Saved list
        {page && (
          <span className="count">
            {narrowed
              ? `${page.total.toLocaleString()} of ${page.total_unfiltered.toLocaleString()} in this list`
              : `${page.total.toLocaleString()} in this list`}
          </span>
        )}
      </h2>
      <div className="panel-body">
        <div className="row-form">
          <div style={{ minWidth: 380 }}>
            <label htmlFor="run">Saved list</label>
            <select
              id="run"
              value={runId ?? ''}
              onChange={(e) => {
                setRunId(Number(e.target.value))
                setOffset(0)
                setExpanded(null)
              }}
              style={{ width: '100%' }}
            >
              {populated.length === 0 && <option value="">no lists with members yet</option>}
              {populated.map((r) => (
                <option key={r.id} value={r.id}>
                  run {r.id} — {r.filter} — {r.run_date} ({r.member_count.toLocaleString()})
                </option>
              ))}
            </select>
          </div>
        </div>

        <AccountFilters
          filters={filters}
          setFilter={setFilter}
          more={more}
          setMore={setMore}
          narrowed={narrowed}
          onClear={() => setFilter(NO_FILTERS)}
          showSource
        />
      </div>

      {populated.length === 0 ? (
        <div className="empty">
          No saved list has members yet. Run a filter on the People tab first.
        </div>
      ) : loading || !page ? (
        <div className="loading">Loading accounts…</div>
      ) : page.members.length === 0 ? (
        <div className="empty">
          Nothing in this list matches those filters.{' '}
          {narrowed && (
            <button className="linkish" onClick={() => setFilter(NO_FILTERS)}>
              Clear them
            </button>
          )}
        </div>
      ) : (
        <>
          <AccountTable
            rows={page.members}
            unmasked={unmasked}
            expanded={expanded}
            setExpanded={setExpanded}
            runId={runId}
          />
          <Pager offset={offset} size={PAGE} total={page.total} onOffset={setOffset} />
        </>
      )}
    </div>
  )
}

/** The filter form, shared by both scopes.
 *
 *  ONE COMPONENT, because the two screens send these to predicates the server
 *  builds in one place (lib/members.py). Two copies of this form is how "paying"
 *  quietly starts meaning two different things on two halves of one tab. */
function AccountFilters({
  filters,
  setFilter,
  more,
  setMore,
  narrowed,
  onClear,
  showSource,
  trailing,
}: {
  filters: Filters
  setFilter: (patch: Partial<Filters>) => void
  more: boolean
  setMore: (v: boolean) => void
  narrowed: boolean
  onClear: () => void
  showSource: boolean
  trailing?: React.ReactNode
}) {
  return (
    <>
      <div className="row-form">
        <div style={{ minWidth: 260 }}>
          <label htmlFor={`q-${showSource}`}>
            Search <span className="label-note">— account id, name, company or domain</span>
          </label>
          <input
            id={`q-${showSource}`}
            type="text"
            value={filters.q}
            placeholder="e.g. 14706, acme, .co.uk"
            onChange={(e) => setFilter({ q: e.target.value })}
          />
        </div>
        <div style={{ width: 180 }}>
          <label htmlFor={`stage-${showSource}`}>Stage</label>
          <select
            id={`stage-${showSource}`}
            value={filters.stage}
            style={{ width: '100%' }}
            onChange={(e) => setFilter({ stage: e.target.value })}
          >
            <option value="">any</option>
            {STAGES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div style={{ width: 120 }}>
          <label htmlFor={`paying-${showSource}`}>Paying</label>
          <select
            id={`paying-${showSource}`}
            value={filters.paying}
            style={{ width: '100%' }}
            onChange={(e) => setFilter({ paying: e.target.value })}
          >
            <option value="">any</option>
            <option value="yes">yes</option>
            <option value="no">no</option>
          </select>
        </div>
        <div style={{ width: 120 }}>
          <label htmlFor={`verified-${showSource}`}>Verified</label>
          <select
            id={`verified-${showSource}`}
            value={filters.verified}
            style={{ width: '100%' }}
            onChange={(e) => setFilter({ verified: e.target.value })}
          >
            <option value="">any</option>
            <option value="yes">yes</option>
            <option value="no">no</option>
          </select>
        </div>
        {showSource && (
          <div style={{ width: 150 }}>
            <label htmlFor="source">Added by</label>
            <select
              id="source"
              value={filters.source}
              style={{ width: '100%' }}
              onChange={(e) => setFilter({ source: e.target.value })}
            >
              <option value="">any</option>
              <option value="filter">the filter</option>
              <option value="manual">by hand</option>
            </select>
          </div>
        )}
        <div>
          <label>&nbsp;</label>
          <button type="button" className="linkish" onClick={() => setMore(!more)}>
            {more ? 'Fewer filters' : 'More filters'}
          </button>
        </div>
        {narrowed && (
          <div>
            <label>&nbsp;</label>
            <button type="button" onClick={onClear}>
              Clear filters
            </button>
          </div>
        )}
        {trailing}
      </div>

      {more && (
        <div className="stack">
          <p className="hint">
            The five below are <strong>derived</strong> values, not columns — they are the vocabulary
            the segment conditions are written in, so "which of these are dormant" is a question
            about a band. Answering one costs a pass over the whole set, about half a second on the
            largest segment, and only when one is set.
          </p>
          <div className="row-form">
            {BANDS.map((band) => (
              <div key={band.key} style={{ width: 150 }}>
                <label htmlFor={`${band.key}-${showSource}`}>{band.label}</label>
                <select
                  id={`${band.key}-${showSource}`}
                  value={filters[band.key]}
                  style={{ width: '100%' }}
                  onChange={(e) => setFilter({ [band.key]: e.target.value })}
                >
                  <option value="">any</option>
                  {band.options.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          <div className="row-form">
            {YES_NO.map((f) => (
              <div key={f.key} style={{ width: 170 }}>
                <label htmlFor={`${f.key}-${showSource}`}>{f.label}</label>
                <select
                  id={`${f.key}-${showSource}`}
                  value={filters[f.key]}
                  style={{ width: '100%' }}
                  onChange={(e) => setFilter({ [f.key]: e.target.value })}
                >
                  <option value="">any</option>
                  <option value="yes">yes</option>
                  <option value="no">no</option>
                </select>
              </div>
            ))}
            {RANGES.map((r) => (
              <div key={r.min} style={{ width: 170 }}>
                <label htmlFor={`${r.min}-${showSource}`}>
                  {r.label} <span className="label-note">— from / to</span>
                </label>
                <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
                  <input
                    id={`${r.min}-${showSource}`}
                    type="number"
                    value={filters[r.min]}
                    onChange={(e) => setFilter({ [r.min]: e.target.value })}
                  />
                  <input
                    type="number"
                    value={filters[r.max]}
                    onChange={(e) => setFilter({ [r.max]: e.target.value })}
                  />
                </div>
              </div>
            ))}
          </div>
          <p className="hint">
            A range never matches a row whose value is null, and null is not zero here — a null "days
            idle" is an account that has never opened the dashboard, not one that opened it today.
          </p>
        </div>
      )}
    </>
  )
}

/** The rows, and the detail that opens under one.
 *
 *  `runId` null means the row came from the index rather than from a list, and
 *  the detail it opens is the run-free one. */
function AccountTable({
  rows,
  unmasked,
  expanded,
  setExpanded,
  runId,
}: {
  rows: (Member | AccountRow)[]
  unmasked: boolean
  expanded: number | null
  setExpanded: (id: number | null) => void
  runId: number | null
}) {
  const span = 5 + COLUMNS.length + (runId == null ? 1 : 0)
  return (
    <table>
      <thead>
        <tr>
          <th className="num">Account</th>
          <th>Name</th>
          <th>Company</th>
          <th>Domain</th>
          {COLUMNS.map((c) => (
            <th key={c.key} className={c.kind === 'num' ? 'num' : undefined}>
              {c.label}
            </th>
          ))}
          <th>Source</th>
          {runId == null && <th>Last cut into</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((m) => {
          const s = m.snapshot as Record<string, unknown>
          const seen = (m as AccountRow).last_seen_in
          return (
            // Fragment needs the key, not the rows inside it: a row plus
            // its expanded detail are two siblings from one item.
            <Fragment key={m.account_id}>
              <tr
                className={`clickable${expanded === m.account_id ? ' selected' : ''}`}
                onClick={() => setExpanded(expanded === m.account_id ? null : m.account_id)}
              >
                <td className="num mono">{m.account_id}</td>
                <td>{show(s.first_name)}</td>
                <td>{show(s.company_name)}</td>
                <td className="mono">{show(s.domain)}</td>
                {COLUMNS.map((c) => (
                  <td key={c.key} className={c.kind === 'num' ? 'num' : undefined}>
                    {show(s[c.key])}
                  </td>
                ))}
                <td>
                  {m.source === 'manual' ? (
                    <span className="badge catchall">added by hand</span>
                  ) : (
                    'filter'
                  )}
                </td>
                {runId == null && (
                  <td className="mono dim" title={seen ? `run ${seen.run_id}` : undefined}>
                    {seen ? `${seen.filter} · ${seen.run_date}` : '—'}
                  </td>
                )}
              </tr>
              {expanded === m.account_id && (
                <tr>
                  <td colSpan={span} className="brief-cell">
                    <AccountDetail
                      runId={runId}
                      accountId={m.account_id}
                      masked={!unmasked}
                    />
                  </td>
                </tr>
              )}
            </Fragment>
          )
        })}
      </tbody>
    </table>
  )
}

function Pager({
  offset,
  size,
  total,
  onOffset,
}: {
  offset: number
  size: number
  total: number
  onOffset: (n: number) => void
}) {
  return (
    <div className="panel-body row">
      <button disabled={offset === 0} onClick={() => onOffset(Math.max(0, offset - size))}>
        Previous
      </button>
      <span className="hint">
        {Math.min(offset + 1, total)}–{Math.min(offset + size, total)} of {total.toLocaleString()}
      </span>
      <button disabled={offset + size >= total} onClick={() => onOffset(offset + size)}>
        Next
      </button>
    </div>
  )
}

/** The form's strings, as the API's types. Empty is "no filter", never "match
 *  the empty string": a select reset to its blank option must widen. */
function toQuery(f: Filters, includeSource: boolean): MemberQuery {
  const tri = (v: string) => (v === '' ? undefined : v === 'yes')
  const num = (v: string) => (v.trim() === '' ? undefined : Number(v))
  return {
    q: f.q || undefined,
    stage: f.stage || undefined,
    source: includeSource ? f.source || undefined : undefined,
    paying: tri(f.paying),
    verified: tri(f.verified),
    activated: tri(f.activated),
    customized: tri(f.customized),
    in_eu: tri(f.in_eu),
    visits_min: num(f.visits_min),
    visits_max: num(f.visits_max),
    consents_min: num(f.consents_min),
    consents_max: num(f.consents_max),
    idle_min: num(f.idle_min),
    idle_max: num(f.idle_max),
    commercial_state: f.commercial_state || undefined,
    funnel_stage: f.funnel_stage || undefined,
    traffic_band: f.traffic_band || undefined,
    engagement_recency: f.engagement_recency || undefined,
    quota_pressure: f.quota_pressure || undefined,
  }
}

/**
 * One account, as the engine sees it.
 *
 * This used to be `JSON.stringify(snapshot)` — thirty raw columns, unordered
 * and unexplained. That is worth having, and it is the wrong thing to lead
 * with: the question actually being asked of a row is "is the rule that
 * selected this person right about them", and no raw column answers it. The
 * DERIVED values do, and each one carries the cut-off it fell on, so
 * `traffic_band: dead` reads as "< 200 visits" rather than as a word.
 *
 * The raw snapshot is still one click away, at the bottom, where it belongs.
 */
function AccountDetail({
  runId,
  accountId,
  masked,
}: {
  /** Null when the row came from the index rather than from a saved list. The
   *  body is the same; only "why they are here" can be answered differently. */
  runId: number | null
  accountId: number
  masked: boolean
}) {
  const [detail, setDetail] = useState<MemberDetail | AccountDetailBody | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showRaw, setShowRaw] = useState(false)

  useEffect(() => {
    let live = true
    setDetail(null)
    setError(null)
    const load =
      runId == null ? api.account(accountId, masked) : api.memberDetail(runId, accountId, masked)
    load
      .then((d) => live && setDetail(d))
      .catch((e) => live && setError(describe(e)))
    return () => {
      live = false
    }
  }, [runId, accountId, masked])

  if (error) return <div className="error">{error}</div>
  if (!detail) return <div className="loading">Reading the account…</div>

  // Three kinds now. The numbers used to fall into `flags` by default, which
  // put "days since last visit: 412" under a heading that called it a
  // yes-or-no fact.
  const bands = detail.signals.filter((s) => s.kind === 'band')
  const numbers = detail.signals.filter((s) => s.kind === 'number')
  const flags = detail.signals.filter((s) => s.kind !== 'band' && s.kind !== 'number')

  return (
    <div className="brief">
      <div className="brief-step">
        <div className="brief-head">
          <span className="brief-n">1</span>
          <strong>
            {Array.isArray(detail.selected_by)
              ? 'Which segments have picked this account up'
              : 'Why this account is in the list'}
          </strong>
        </div>
        <p className="hint brief-cap">
          {Array.isArray(detail.selected_by)
            ? 'Every list this account has been cut into, newest first. Everything below is derived from the newest of those snapshots — a picture of them as they were when that list was cut, not the CRM as it stands now.'
            : 'The condition that selected them, and the values it was reading. Everything below is derived from the snapshot taken when the list was cut — not from the CRM as it stands now, which is what makes a saved list reproducible.'}
        </p>
        {Array.isArray(detail.selected_by) ? (
          <table className="tight">
            <tbody>
              {(detail.selected_by as SelectedBy[]).map((s) => (
                <tr key={`${s.run_id}`}>
                  <td className="mono clause-name">{s.name}</td>
                  <td className="dim">{s.run_date}</td>
                  <td className="mono clause-val">{s.when ?? 'catch-all — no condition'}</td>
                  <td>
                    {s.source === 'manual' && <span className="badge catchall">by hand</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <dl className="facts">
            <dt>Filter</dt>
            <dd className="mono">{detail.selected_by.name}</dd>
            <dt>Condition</dt>
            <dd className="mono">{detail.selected_by.when ?? 'catch-all — no condition'}</dd>
            <dt>Added</dt>
            <dd>
              {new Date(detail.created_at).toLocaleString()}{' '}
              {detail.source === 'manual' ? (
                <span className="badge catchall">by hand</span>
              ) : (
                <span className="dim">by the filter</span>
              )}
            </dd>
          </dl>
        )}
      </div>

      <div className="brief-step">
        <div className="brief-head">
          <span className="brief-n">2</span>
          <strong>What the engine derived about them</strong>
        </div>
        <p className="hint brief-cap">
          Bands first — a band is a number turned into a word, and the rule beside it is the
          cut-off this account actually fell on. Then the raw counts a condition can bound
          directly, then the flags, which are yes-or-no facts.
        </p>
        <table className="tight">
          <tbody>
            {bands.map((s) => (
              <SignalRow key={s.name} signal={s} />
            ))}
          </tbody>
        </table>
        {numbers.length > 0 && (
          <>
            <div className="clause-rule" style={{ marginTop: 'var(--s2)' }}>
              <strong>Counts and ages</strong>{' '}
              <span className="dim">
                — an empty value means the thing never happened, which no period matches
              </span>
            </div>
            <table className="tight">
              <tbody>
                {numbers.map((s) => (
                  <SignalRow key={s.name} signal={s} />
                ))}
              </tbody>
            </table>
          </>
        )}
        <div className="clause-rule" style={{ marginTop: 'var(--s2)' }}>
          <strong>Flags</strong>
        </div>
        <table className="tight">
          <tbody>
            {flags.map((s) => (
              <SignalRow key={s.name} signal={s} />
            ))}
          </tbody>
        </table>
      </div>

      <div className="brief-step">
        <div className="brief-head">
          <span className="brief-n">3</span>
          <strong>
            {detail.history.length === 0
              ? 'We have never written to them'
              : `We have written to them ${detail.history.length} time${detail.history.length === 1 ? '' : 's'}`}
          </strong>
        </div>
        <p className="hint brief-cap">
          Every outcome, not only the sends. "We tried and suppression stopped it" and "we never
          wrote" are different facts, and only one of them means they saw something.
        </p>
        {detail.history.length > 0 && (
          <table className="tight">
            <tbody>
              {detail.history.map((h, i) => (
                <tr key={i}>
                  <td>{new Date(h.sent_at).toLocaleDateString()}</td>
                  <td>
                    <span className={`badge ${h.outcome === 'sent' ? 'ok' : 'parked'}`}>
                      {h.outcome}
                    </span>
                  </td>
                  <td className="mono dim">{h.filter ?? '—'}</td>
                  <td>{h.subject ?? h.reason ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="brief-step">
        <div className="brief-head">
          <span className="brief-n">4</span>
          <strong>
            {detail.drafts.length === 0
              ? 'Nothing has been composed for them'
              : `${detail.drafts.length} email${detail.drafts.length === 1 ? '' : 's'} composed for them`}
          </strong>
        </div>
        <p className="hint brief-cap">
          What was WRITTEN, which is a different record from what was sent — a draft exists for
          plenty of people we never wrote to. The sent record above deliberately keeps no body:
          this schema holds no personal prose, so it stores the subject and a hash instead.
          {detail.masked && ' Bodies are hidden while identity is masked; prose carries the name and the domain and cannot be masked without being mangled.'}
        </p>
        {detail.drafts.length > 0 && (
          <table className="tight">
            <tbody>
              {detail.drafts.map((d) => (
                <ComposedRow key={d.draft_id} draft={d} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="brief-step">
        <div className="brief-head">
          <span className="brief-n">5</span>
          <strong>What could we send them?</strong>
        </div>
        <AdvicePanel
          runId={runId}
          accountId={accountId}
          askedAs={
            Array.isArray(detail.selected_by)
              ? (detail.selected_by as SelectedBy[])[0]?.name
              : detail.selected_by.name
          }
        />
      </div>

      <div className="brief-foot">
        <button className="linkish" onClick={() => setShowRaw(!showRaw)}>
          {showRaw ? 'Hide the raw snapshot' : 'Show the raw snapshot'}
        </button>{' '}
        — every column exactly as the signal query returned it, which is what to read when a
        derived value looks wrong.
        {showRaw && <pre className="notes">{JSON.stringify(detail.snapshot, null, 2)}</pre>}
      </div>
    </div>
  )
}

/** One composed email, with its body behind a click.

    Thirty of these open at once is a wall of prose, and the question a row
    usually answers is "what was this one about" — which the subject answers. */
function ComposedRow({ draft }: { draft: ComposedEmail }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <tr>
        <td className="mono dim">{new Date(draft.created_at).toLocaleDateString()}</td>
        <td>
          <span className={`badge ${draft.status === 'approved' ? 'ok' : 'parked'}`}>
            {draft.status}
          </span>
        </td>
        <td className="mono dim">{draft.campaign}</td>
        <td>{draft.subject}</td>
        <td>
          {draft.refined && <span className="badge catchall">refined</span>}
        </td>
        <td>
          {draft.body ? (
            <button className="linkish" onClick={() => setOpen(!open)}>
              {open ? 'hide' : 'read'}
            </button>
          ) : (
            <span className="dim" title="Unmask the list to read the body.">
              masked
            </span>
          )}
        </td>
      </tr>
      {open && draft.body && (
        <tr>
          <td colSpan={6}>
            <pre className="notes">{draft.body}</pre>
          </td>
        </tr>
      )}
    </>
  )
}

/**
 * Asking the model what Reach Out could send this person.
 *
 * THIS WRITES NOTHING, and the panel says so, because every other button on
 * this screen that calls a model does change something. It is a briefing —
 * one or more suggested emails, from the campaigns this account already
 * matches — not G0's skip-by-default catch-all. The CTA is still picked from
 * a fixed list, and the content validator still runs on every suggestion.
 *
 * `skip` is a real answer when there is genuinely nothing to send. A
 * validator refusal is the guard working, and what it refused is shown.
 */
function AdvicePanel({
  runId,
  accountId,
  askedAs,
}: {
  /** Null asks the run-free endpoint, which picks the segment itself. */
  runId: number | null
  accountId: number
  /** Which segment's brief to ask in the voice of, when there is no run to
   *  take it from. Undefined lets the server choose. */
  askedAs?: string
}) {
  const [advice, setAdvice] = useState<Advice | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ask = async () => {
    setBusy(true)
    setError(null)
    setAdvice(null)
    try {
      setAdvice(
        runId == null
          ? await api.adviseAccount(accountId, askedAs)
          : await api.advise(runId, accountId),
      )
    } catch (err) {
      // WITH ITS STATUS. "Ask the model is broken" was unanswerable from a
      // screenshot: a 400 is a refusal that explains itself, a 502 is the
      // request outliving the proxy, and a 500 is a traceback in the log.
      setError(describe(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="stack">
      <div className="row">
        <button className="primary" disabled={busy} onClick={() => void ask()}>
          {busy ? (
            <span className="row" style={{ gap: 7 }}>
              <span className="spinner" /> Asking…
            </span>
          ) : advice ? (
            'Ask again'
          ) : (
            'Suggest emails'
          )}
        </button>
        <span className="hint">
          Reads this account's data, the campaigns they match, and everything we have already sent
          them. Creates no draft and changes nothing — the answer is one or more suggestions on
          this screen.
        </span>
      </div>

      {error && <div className="error">{error}</div>}

      {advice && !advice.ok && (
        <div className="error">
          <strong>The answer was refused.</strong> The same validator that guards every refinement
          ran on it and found:
          <ul className="bullets tight">
            {(advice.problems ?? []).map((p) => (
              <li key={p} style={{ fontSize: 12 }}>
                {p}
              </li>
            ))}
          </ul>
          {advice.rejected_output?.subject && (
            <div className="clause-rule">
              It wanted to send: <span className="mono">{advice.rejected_output.subject}</span>
            </div>
          )}
        </div>
      )}

      {advice?.ok && advice.verdict === 'skip' && (
        <div className="notice">
          <div className="row">
            <span className="badge parked">skip</span>
            <strong>Nothing worth writing</strong>
          </div>
          <p className="hint caption">{advice.reason}</p>
        </div>
      )}

      {advice?.ok && advice.verdict === 'send' && (
        <div className="stack">
          {advice.reason && (
            <p className="hint caption" style={{ margin: 0 }}>
              {advice.reason}
            </p>
          )}
          {(advice.suggestions?.length ? advice.suggestions : [
            {
              title: 'suggested email',
              why: advice.reason ?? '',
              campaign: '',
              subject: advice.subject ?? '',
              body: advice.body ?? '',
              cta: advice.cta ?? '',
              signals: advice.signals ?? [],
              caution: '',
            },
          ]).map((s, i) => (
            <div className="notice" key={`${s.subject}-${i}`}>
              <div className="row">
                <span className="badge ok">
                  {advice.suggestions && advice.suggestions.length > 1
                    ? `${i + 1} of ${advice.suggestions.length}`
                    : 'send'}
                </span>
                <strong>{s.title || s.subject}</strong>
                {s.campaign ? <span className="mono dim">{s.campaign}</span> : null}
              </div>
              {s.why && <p className="hint caption">{s.why}</p>}
              <div className="cond mono" style={{ marginTop: 'var(--s2)' }}>
                {s.subject}
              </div>
              <pre className="notes">{s.body}</pre>
              <div className="clause-rule">
                Button: <span className="mono clause-val">{s.cta}</span>
                {s.signals?.length ? ` · from ${s.signals.join(', ')}` : ''}
              </div>
              {s.caution ? (
                <p className="hint caption">Do not: {s.caution}</p>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {advice?.ok && (advice.rejected_suggestions?.length ?? 0) > 0 && (
        <div className="hint caption">
          {advice.rejected_suggestions!.length} other suggestion
          {advice.rejected_suggestions!.length === 1 ? '' : 's'} failed the validator and
          {' '}was not shown.
        </div>
      )}

      {advice && (
        <p className="hint">
          {advice.model} · asked as{' '}
          <span className="mono">{advice.asked_as.filter}</span> · read{' '}
          {advice.history_considered} previous email
          {advice.history_considered === 1 ? '' : 's'} · nothing was stored.
        </p>
      )}
    </div>
  )
}


function SignalRow({ signal }: { signal: Signal }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <tr>
        <td className="mono clause-name">{signal.name}</td>
        <td>
          <span className="mono clause-val">{show(signal.value)}</span>
        </td>
        <td className="dim">
          {signal.rule ?? ''}
          {signal.unit && signal.rule ? ` (${signal.unit})` : ''}
        </td>
        <td className="dim mono">{signal.source ?? ''}</td>
        <td>
          {signal.note && (
            <button className="linkish" onClick={() => setOpen(!open)}>
              {open ? 'hide' : 'why'}
            </button>
          )}
        </td>
      </tr>
      {open && signal.note && (
        <tr>
          <td colSpan={5}>
            <pre className="notes">{signal.note.trim()}</pre>
          </td>
        </tr>
      )}
    </>
  )
}

/** NULL means something specific in this data and must not read as zero or blank.
 *  `days_since_visit` null is "never opened the dashboard", not "today"; a
 *  null quota is "unlimited", not "no allowance". Showing an explicit marker
 *  keeps that distinction visible on screen. */
function show(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'boolean') return value ? 'yes' : 'no'
  if (typeof value === 'number') return value.toLocaleString()
  return String(value)
}
